"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, Lock, Mic, Video } from "lucide-react";
import { Popover } from "@speakai/ui/popover";
import { useTranslation } from "react-i18next";
import { formatClock } from "@speakai/common/src/utils/clock";
import { cn } from "@/lib/utils/cn";
import { FILTER_TRIGGER_CLASS } from "@/components/shared/filters/filter-select";
import { MAX_RECORDING_SECONDS } from "@/features/recorder/hooks/use-recording-engine";

const FOCUSABLE_SELECTOR = "button, select, input, [tabindex]:not([tabindex='-1'])";
const PORTALED_LIST_SELECTOR = '[role="listbox"]';

interface DevicesChipProps {
  micLabel: string;
  recordingState: "idle" | "recording" | "paused";
  elapsedTime: number;
  /** Shows the camera icon next to the mic in Video and Screen mode. */
  withCamera?: boolean;
  /** Device controls shown in the popover while idle. */
  children: ReactNode;
  className?: string;
}

export function DevicesChip({ micLabel, recordingState, elapsedTime, withCamera = false, children, className }: DevicesChipProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pressedInListRef = useRef(false);
  const isLocked = recordingState !== "idle";

  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }, [open]);

  useEffect(() => {
    if (isLocked) setOpen(false);
  }, [isLocked]);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      pressedInListRef.current = e.target instanceof Element && e.target.closest(PORTALED_LIST_SELECTOR) !== null;
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    return () => document.removeEventListener("mousedown", handleMouseDown, true);
  }, [open]);

  const cameraIcon = withCamera ? <Video className="h-4 w-4 shrink-0" aria-hidden="true" /> : null;

  if (isLocked) {
    return (
      <span
        aria-disabled="true"
        className={cn(FILTER_TRIGGER_CLASS, "max-w-full cursor-default overflow-hidden whitespace-nowrap tabular-nums text-muted-foreground hover:bg-background", className)}
        data-testid="recorder-status-line"
      >
        <span className="inline-flex shrink-0 items-center gap-2 font-medium text-foreground">
          <span
            className={cn(
              "h-2 w-2 rounded-full bg-record",
              recordingState === "recording" && "animate-pulse motion-reduce:animate-none",
            )}
            aria-hidden="true"
          />
          {recordingState === "paused"
            ? t("RECORD.STATUS_PAUSED", "Paused")
            : t("RECORD.STATUS_RECORDING", "Recording")}
        </span>
        <span className="hidden h-3.5 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
        <span className="hidden min-w-0 items-center gap-1.5 text-foreground sm:inline-flex">
          <Mic className="h-4 w-4 shrink-0" aria-hidden="true" />
          {cameraIcon}
          <span className="min-w-0 max-w-[160px] truncate" title={micLabel}>{micLabel}</span>
        </span>
        <span className="hidden h-3.5 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
        <span className="shrink-0">
          {t("RECORD.TIME_LEFT", "{{time}} left", {
            time: formatClock(MAX_RECORDING_SECONDS - elapsedTime, { padMinutes: false }),
          })}
        </span>
        <Lock className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />
      </span>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next && pressedInListRef.current) {
          pressedInListRef.current = false;
          return;
        }
        if (!next && (document.activeElement === document.body || panelRef.current?.contains(document.activeElement))) {
          chipRef.current?.focus();
        }
        setOpen(next);
      }}
      align="end"
      className={className}
      trigger={
        <button
          ref={chipRef}
          type="button"
          aria-label={t("RECORD.DEVICES", "Devices")}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={micLabel}
          className={FILTER_TRIGGER_CLASS}
          data-testid="recorder-devices-chip"
        >
          <span className="inline-flex shrink-0 items-center gap-1 text-muted-foreground">
            <Mic className="h-4 w-4 shrink-0" aria-hidden="true" />
            {cameraIcon}
          </span>
          <span className="hidden truncate text-foreground sm:inline">{micLabel}</span>
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      }
    >
      <div
        ref={panelRef}
        className="flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-3 p-4"
        aria-label={t("RECORD.DEVICES", "Devices")}
        data-testid="recorder-devices-panel"
      >
        <p className="text-sm font-semibold text-foreground">{t("RECORD.DEVICES", "Devices")}</p>
        {children}
      </div>
    </Popover>
  );
}
